import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, HostBinding, Input, LOCALE_ID, inject} from '@angular/core';
import {ReplaySubject, Subject, combineLatest, map, takeUntil} from 'rxjs';
import {EventDataService} from '../event-data.service';

@Component({
    selector: 'nexus-timer',
    templateUrl: './timer.component.html',
    styleUrls: ['./timer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe]
})
export class TimerComponent {
  private readonly locale = inject(LOCALE_ID);
  private readonly eventDataService = inject(EventDataService);

  @Input() timestamp: number|undefined|null = 0;
  @Input() format: 'team-queue-timer'|'fta'|'queuer-queue-timer'|'approximate-past'|'near-future'|'admin'|'future-short'|'cycle'|'practice-field' = 'team-queue-timer';
  @Input() adminColor = false;

  @HostBinding('class') classes = '';

  private readonly timestampChanged$ = new ReplaySubject<void>(1);
  readonly time$ =  combineLatest([this.eventDataService.nowTimer(), this.timestampChanged$]);
  readonly display$ = this.time$.pipe(map(([now]) => this.timestamp ? this.getDisplay(now) : ''));

  private readonly timeFormat = new Intl.DateTimeFormat(this.locale, {hour: 'numeric', minute:'2-digit'});

  private readonly onDestroy$ = new Subject<void>();

  ngOnInit() {
    if(!this.adminColor) return;

    this.time$.pipe(takeUntil(this.onDestroy$)).subscribe(([now]) => {
      this.updateColor(now);
    });
  }

  ngOnChanges() {
    this.timestampChanged$.next();
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  private getDisplay(now: number): string {
    switch(this.format) {
      case 'team-queue-timer': return this.getTeamQueueDisplay(now);
      case 'queuer-queue-timer': return this.getQueuerQueueDisplay(now);
      case 'near-future': return this.getNearFutureDisplay(now);
      case 'approximate-past': return this.getApproximatePastDisplay(now);
      case 'fta': return this.getFtaDisplay(now);
      case 'practice-field': return this.getPracticeFieldDisplay(now);
      case 'admin': return this.getAdminDisplay(now);
      case 'future-short': return this.getFutureShort(now);
      case 'cycle': return this.getCycle(now);
      default: return this.getTeamQueueDisplay(now);
    }
  }

  private updateColor(now: number) {
    if(!this.adminColor || !this.timestamp) {
      this.classes = '';
      return;
    }

    if(now - this.timestamp > 45 * 60_000) {
      this.classes = 'text-danger';
      return;
    }

    if(now - this.timestamp > 20 * 60_000) {
      this.classes = 'text-warning';
      return;
    }

    this.classes = '';
  }

  private getTeamQueueDisplay(now: number) {
    const diffSeconds = (this.timestamp! - now) / 1000;
    const diffMinutes = diffSeconds / 60;
  
    if (diffMinutes < 2) {
      return $localize `Queuing soon`;
    }
  
    if(diffMinutes >= 60) {
      return $localize `Queuing ~${this.timeFormat.format(this.timestamp!)}`;
    }
    
    return $localize `Queuing ~${Math.round(diffMinutes)} mins`;
  }

  private getQueuerQueueDisplay(now: number) {
    const diffSeconds = Math.round((this.timestamp! - now) / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);

    if (diffSeconds < 30) {
      return 'Expected soon';
    }

    if(diffMinutes < 60) {
      return `${diffMinutes}:${(diffSeconds % 60).toString().padStart(2, '0')}`;
    }

    return this.timeFormat.format(this.timestamp!);
  }

  private getNearFutureDisplay(now: number) {
    const diffSeconds = (this.timestamp! - now) / 1000;

    if (diffSeconds < 90) {
      return $localize `very soon`;
    }

    if (diffSeconds / 60 > 60) {
      return $localize `@ ~${this.timeFormat.format(this.timestamp!)}`;
    }

    return $localize `in ~${Math.round(diffSeconds / 60)} minutes`;
  }

  private getApproximatePastDisplay(now: number) {
    const diffSeconds = (now - this.timestamp!) / 1000;

    if (diffSeconds < 45) {
      return $localize `just now`;
    }

    if (diffSeconds < 90) {
      return $localize `a minute ago`;
    }

    if (diffSeconds / 60 > 120) {
      return $localize `a long time ago`;
    }

    if (diffSeconds / 60 > 75) {
      return $localize `over an hour ago`;
    }
    
    if (diffSeconds / 60 > 50) {
      return $localize `an hour ago`;
    }

    return $localize `${Math.round(diffSeconds / 60)} minutes ago`;
  }

  private getFtaDisplay(now: number) {
    const diffSeconds = (this.timestamp! - now) / 1000;

    const hours = Math.floor(Math.abs(diffSeconds) / 60 / 60);
    const mins = String(Math.floor(Math.abs(diffSeconds) % (3600) / 60)).padStart(hours ? 2 : 1, '0');
    const secs = String(Math.floor(Math.abs(diffSeconds) % 60)).padStart(2, '0');

    let prefix = '';
    if(Math.ceil(diffSeconds) < 0) {
      prefix = '-';
    }

    return `${prefix}${hours ? (hours + ':') : ''}${mins}:${secs}`;
  }

  private getPracticeFieldDisplay(now: number) {
    const diffSeconds = (this.timestamp! - now) / 1000;

    const mins = Math.floor(Math.abs(diffSeconds) / 60);
    const secs = String(Math.floor(Math.abs(diffSeconds) % 60)).padStart(2, '0');

    if(diffSeconds < 0) {
      return '0:00';
    }

    return `${mins}:${secs}`;
  }

  private getAdminDisplay(now: number) {
    const diff = this.timestamp! - now; // negative if in past
    const diffSeconds = Math.round(Math.abs(diff) / 1000);
    const diffMinutes = Math.round(diffSeconds / 60);
    const diffHours = Math.round(diffMinutes / 60);
    const diffDays = Math.round(diffHours / 24);

    if (diff < 0) {
      if(diffSeconds < 60) {
        return 'Just now';
      }
      if(diffMinutes < 60) {
        return `${diffMinutes}m ago`;
      }
      if(diffHours < 24) {
        return `${diffHours}h ago`;
      }
      return `${diffDays}d ago`;
    }

    if(diffSeconds < 60) {
      return 'Very soon';
    }
    if(diffMinutes < 60) {
      return `in ${diffMinutes}m`;
    }
    if(diffHours < 24) {
      return `in ${diffHours}h`;
    }
    return `in ${diffDays}d`;
  }

  private getFutureShort(now: number) {
    const diff = this.timestamp! - now;
    const diffSeconds = Math.round(Math.abs(diff) / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffSeconds / 60 / 60);

    if(diffSeconds < 30) {
      return 'now';
    }
    if(diffMinutes < 60) {
      return `~${diffMinutes}m`;
    }
    return `~${diffHours}h ${diffMinutes % 60}m`;
  }

  private getCycle(now: number) {
    const diffSeconds = (now - this.timestamp!) / 1000;

    const mins = Math.floor(Math.abs(diffSeconds) / 60);
    const secs = String(Math.floor(Math.abs(diffSeconds) % 60)).padStart(2, '0');

    return `${mins}:${secs}`;
  }
}
